import React, { useState, useEffect, useRef } from 'react';
import { useAriaModal } from 'lib/Hooks/accessibility/useAriaModal';
import classNames from 'classnames';
import { GammaVerticalVideoPlayer } from 'components/Gamma/VerticalVideoPlayer';
import { useShouldPlayAd } from 'components/Gamma/VerticalVideoPlayer/utils/useShouldPlayAd';
import { VideoCardCarousel } from 'components/VerticalVideo/VideoCardCarousel';
import { VideoOverlay } from 'components/VerticalVideo/VideoOverlay';
import { VerticalSnapScroller } from 'components/VerticalVideo/VerticalSnapScroller';
import { isTouchSupportedDevice, getWrappedIndex } from 'components/VerticalVideo/utils';
import styles from './styles.module.scss';

const SHOW_AD_ON_FIRST_PLAY = false;

const gammaConfig = {
  controls: true,
  aspectratio: '9:16',
  controlBar: {
    layout: [
      { name: 'time' },
    ],
  },
  stretching: 'fill',
  height: 560,
  displayPlaybackLabel: true,
  playbackRateControls: true,
  renderCaptionsNatively: false,
  repeat: true,
  skin: {
    name: 'gamma',
    active: '#3061FF',
    inactive: '#FFFFFF',
  },
};

/**
 *
 * @param {object} props
 * @param {object} props.content
 */
export const VerticalVideo = ({ content }) => {
  const [currentIndex, setCurrentIndex] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [nextVideoShouldShowAd, setNextVideoShouldShowAd] = useState(SHOW_AD_ON_FIRST_PLAY);
  const [isTouchSupported, setIsTouchSupported] = useState(false);
  const firstFocusableElementRef = useRef(null);
  const {
    shouldPlayAd,
    registerVideoPlay,
    registerAdPlayed,
  } = useShouldPlayAd({ adInterval: 7 });

  const {
    items: unfilteredItems,
  } = content;

  const items = unfilteredItems?.filter(
    (i) => Boolean(i) && (i.type !== 'product' || (Boolean(i.id) && Boolean(i.item))),
  );

  const selectedVideo = items[currentIndex]?.item || null;

  useAriaModal({
    isVisible: selectedVideo && isPlayerVisible,
    primaryFocusElementRef: firstFocusableElementRef,
  });

  useEffect(() => {
    setIsTouchSupported(isTouchSupportedDevice());
  }, []);

  useEffect(() => {
    if (selectedVideo && isPlayerVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedVideo, isPlayerVisible]);

  /**
   * Function that handles moving the to the previous video.
   * It will wrap around to the end of the list
   */
  const handlePrevious = () => {
    const prevIndex = getWrappedIndex(currentIndex - 1, items.length);
    setCurrentIndex(prevIndex);
    setNextVideoShouldShowAd(shouldPlayAd);
    setIsPlayerVisible(true);
  };

  /**
   * Function that handles moving to the next video.
   * It will wrap around to the start of the list
   */
  const handleNext = () => {
    const nextIndex = getWrappedIndex(currentIndex + 1, items.length);
    setCurrentIndex(nextIndex);
    setNextVideoShouldShowAd(shouldPlayAd);
    setIsPlayerVisible(true);
  };

  /**
   * Handles closing the video overlay with a transition.
   */
  const closeOverlay = () => {
    setIsClosing(true);
    setTimeout(() => {
      setCurrentIndex(null);
      setIsPlayerVisible(false);
      setIsClosing(false);
    }, 300); // Match the CSS transition duration
  };

  return (
    <section
      className={classNames(styles.vertVideoPackage, { [styles.locked]: selectedVideo })}
      data-testid="vertVideoPackage"
      data-packageid={content.id}
      data-activity-map="vertical_video"
    >
      <VideoCardCarousel
        content={content}
        videoCardClicked={(index) => {
          setCurrentIndex(index);
          setIsPlayerVisible(true);
        }}
      />
      <VideoOverlay
        isVisible={selectedVideo && isPlayerVisible}
        isClosing={isClosing}
        closeOverlay={closeOverlay}
        videoComponent={
          (
            <VerticalSnapScroller
              videoItems={items}
              activeVideoIndex={currentIndex}
              isTouchSupported={isTouchSupported}
              onActiveChange={(direction) => {
                if (direction === 'next') {
                  const nextIndex = getWrappedIndex(currentIndex + 1, items.length);
                  setCurrentIndex(nextIndex);
                } else if (direction === 'previous') {
                  const prevIndex = getWrappedIndex(currentIndex - 1, items.length);
                  setCurrentIndex(prevIndex);
                }
              }}
              videoComponent={
                (
                  <div className={styles.scopedPlayerWrapper}>
                    <GammaVerticalVideoPlayer
                      primaryFocusElementRef={firstFocusableElementRef}
                      autoPlay
                      continuousPlay={false}
                      isTouchSupported={isTouchSupported}
                      video={selectedVideo}
                      config={gammaConfig}
                      useEmbeddedAds={false}
                      handleVideoStart={() => {
                        registerVideoPlay(selectedVideo.id);
                      }}
                      handlePrevious={handlePrevious}
                      handleNext={handleNext}
                      handleClose={closeOverlay}
                      handeAdPlayed={() => {
                        registerAdPlayed();
                        setNextVideoShouldShowAd(false);
                      }}
                      shouldPlayAd={nextVideoShouldShowAd}
                      playlistIndex={items.findIndex((i) => i.item.id === selectedVideo?.id)}
                      playlistId={selectedVideo?.associatedVideoPlaylist?.id}
                    />
                  </div>
                )
              }
            />
          )
        }
      />
    </section>
  );
};
